import { createFileRoute } from '@tanstack/react-router';
import { DocTable, RolePill, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';

export const Route = createFileRoute('/documentation/admission-tokens')({
  component: AdmissionTokensPage,
});

function AdmissionTokensPage() {
  return (
    <>
      <div className={styles.breadcrumb}>Building with Interlucent</div>
      <h1 className={styles.pageTitle}>Tokens</h1>
      <p className={styles.pageSubtitle}>
        How tokens control what a session is allowed to do, and what happens
        when the platform processes them.
      </p>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="how-tokens-work">
          How Tokens Work
        </h2>
        <p className={styles.prose}>
          An admission token declares what a session is allowed to do. The
          platform will only grant access within these bounds. When the token is
          exchanged, the platform refines it further — resolving versions,
          generating identities, and applying policy — but never broadens it.
        </p>
        <InfoCallout variant="info">
          In Interlucent, an <strong>agent</strong> is any participant in a
          session: a browser viewing the stream, or a GPU worker running the
          application.
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="what-this-means">
          What This Means for Browser Agents
        </h2>
        <ul className={styles.featureList}>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Which app?</span>{' '}
            The <code className={styles.mono}>apr</code> claim names the
            application (and optionally version). If the token says{' '}
            <code className={styles.mono}>app_id: "PFfFuw"</code>, the platform
            will only stream that application — not any other application in your
            project.
          </li>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>How patient?</span>{' '}
            The <code className={styles.mono}>tol</code> claim sets timing
            expectations.{' '}
            <code className={styles.mono}>
              queue_wait_tolerance_seconds: 60
            </code>{' '}
            means the browser waits up to 60s for a GPU.{' '}
            <code className={styles.mono}>
              flexible_presence_allowance_seconds: 120
            </code>{' '}
            means a brief network drop won't destroy the session — the seat is
            held for 2 minutes.
          </li>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Who asked?</span>{' '}
            The <code className={styles.mono}>ref</code> claim is your tracking
            handle. Set it to a user ID, order ID, or any string meaningful to
            your system. It flows through to session analytics for billing
            reconciliation.
          </li>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>How fast?</span>{' '}
            The <code className={styles.mono}>sjr</code> flag submits the GPU
            availability request during token exchange itself, shaving one
            round-trip off the connection flow.
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="claims-reference">
          Claims Reference
        </h2>
        <p className={styles.prose}>
          Every claim is a constraint: the platform will only grant access
          within these bounds.
        </p>
        <DocTable
          columns={[
            { header: 'Claim', accessor: 'claim', mono: true, nowrap: true },
            { header: 'Name', accessor: 'name' },
            { header: 'Roles', accessor: 'roles' },
            { header: 'Description', accessor: 'description' },
          ]}
          data={[
            { claim: 'aud', name: 'Audience', roles: 'All', description: '"a"' },
            { claim: 'typ', name: 'Token Type', roles: 'All', description: '"adm"' },
            { claim: 'exp', name: 'Expiration', roles: 'All', description: 'Unix timestamp — admission window closes' },
            { claim: 'iat', name: 'Issued At', roles: 'All', description: 'Unix timestamp' },
            { claim: 'rol', name: 'Role', roles: 'All', description: '"ba" (browser agent) / "wa" (worker agent) / "sc" (session collaborator)' },
            { claim: 'apr', name: 'Application Request', roles: <RolePill role="BA" />, description: '{ app_id, version_id?, custom_args?, psi? } — which app to stream' },
            { claim: 'tol', name: 'Tolerances', roles: 'BA, WA', description: 'Timing constraints (see Tolerances)' },
            { claim: 'ref', name: 'Reference', roles: 'All', description: 'Opaque customer tracking string' },
            { claim: 'sjr', name: 'Swift Job Request', roles: <RolePill role="BA" />, description: 'Submit GPU availability request during exchange' },
            { claim: 'lat', name: 'Latitude', roles: 'All', description: 'Geolocation constraint/override' },
            { claim: 'lng', name: 'Longitude', roles: 'All', description: 'Geolocation constraint/override' },
          ]}
        />

        <h3 className={styles.subsectionTitle} id="worker-claims">
          Worker-Specific Claims
        </h3>
        <p className={styles.prose}>
          The following claims are for worker tokens, typically managed by
          Interlucent or your infrastructure team. Most integrations do not need
          to set these directly.
        </p>
        <DocTable
          columns={[
            { header: 'Claim', accessor: 'claim', mono: true, nowrap: true },
            { header: 'Name', accessor: 'name' },
            { header: 'Description', accessor: 'description' },
          ]}
          data={[
            { claim: 'wtk', name: 'Worker Task', description: '{ t, p? } — what work to perform' },
            { claim: 'pol', name: 'Pool ID', description: 'Which pool provisions this worker' },
            { claim: 'wrc', name: 'Worker Constraints', description: '{ a?: [{ app_id, version_id? }] } — limit which apps this worker can serve' },
          ]}
        />

        <InfoCallout variant="info">
          The <code className={styles.mono}>kid</code> header (not a claim)
          identifies which project signing key was used. The platform resolves
          this to your project and organization during exchange. You can find
          your project signing key in{' '}
          <strong>Console &gt; Project Settings &gt; API Keys</strong>.
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="token-exchange">
          What Happens at Token Exchange
        </h2>
        <p className={styles.prose}>
          When an agent connects, the platform exchanges the admission token for
          an access token. This process refines the admission constraints
          without broadening them:
        </p>
        <ul className={styles.featureList}>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Application</span>{' '}
            The <code className={styles.mono}>apr</code> claim is validated
            against your project's application registry. If a version is
            specified, it must exist. If omitted, the current active version is
            resolved.
          </li>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Identity</span>{' '}
            The platform generates a unique agent ID and session ID for each
            connection, and resolves your project and organization from the
            signing key.
          </li>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Geolocation</span>{' '}
            Latitude and longitude follow a priority chain:{' '}
            <code className={styles.mono}>token</code> &gt;{' '}
            <code className={styles.mono}>request body</code> &gt;{' '}
            <code className={styles.mono}>automatic IP geolocation</code>.
          </li>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Tolerances &amp; Reference</span>{' '}
            These are passed through from the admission token without
            modification. Defaults are applied when the agent joins the session,
            not at exchange time.
          </li>
        </ul>
        <p className={styles.prose}>
          The resulting access token is signed by the platform, carries a
          1-hour expiration, and includes everything needed to authorize and
          route the connection. Your code never needs to inspect or construct
          access tokens — the platform handles this automatically.
        </p>
      </section>
    </>
  );
}
